<template>
  <div class="products">
    <div
      class="products__top-banner container background-container mt-4"
      :class="isBannerEnabled ? 'banner' : ''"
    >
      <AdBanner v-if="isBannerEnabled" type="cookbooks" />
      <div v-else class="row mx-0">
        <b-col md="5" offset-md="1">
          <p class="font-weight-bold mb-1">{{ textAbove }}</p>
          <h1>{{ header }}</h1>
        </b-col>
        <b-col md="5" offset-md="1">
          <div class="products__category-desc">
            {{ category.description }}
          </div>
        </b-col>
      </div>

      <ShareButton />
    </div>

    <div v-if="products && products.length" class="page container mt-5">
      <CarouselView
        v-if="trendingProducts && trendingProducts.length"
        id="trendingProducts"
        class="my-4"
        :items="trendingProducts"
        :mode="CarouselModes.PRODUCT"
        show-default-item-actions
        @click="handleProductClick"
      >
        <template #title>
          <h2>Trending {{ category.name }}</h2>
        </template>
      </CarouselView>

      <CarouselView
        v-if="newProducts && newProducts.length"
        id="newProducts"
        class="mt-5"
        :items="newProducts"
        :mode="CarouselModes.PRODUCT"
        show-default-item-actions
        @click="handleProductClick"
      >
        <template #title>
          <h2>New in {{ category.name }}</h2>
        </template>
      </CarouselView>

      <PreviewItemsView
        v-if="products && products.length"
        class="mt-5"
        :items="products"
        :total="productsTotal"
        :loading="newProductsLoading"
        :filter-value="filterValue"
        :filtered-values-loading="filteredProductsLoading"
        :is-nothing-found="isNothingFound"
        not-found-message="Books not found"
        :title="`All ${category.name}`"
        type="products"
        @click="handleProductClick"
        @onViewMoreClick="getNewProducts"
        @onFilterValueChange="handleFilterValueChange"
      />
    </div>

    <div v-else>
      <TitleWithBackground
        :lines="['Coming', 'soon']"
        :mobile-split="false"
        variant="danger"
        direction="left"
        class="my-5"
      />
    </div>
  </div>
</template>

<script>
import { filter, get, isEmpty } from 'lodash';
import { mapGetters } from 'vuex';
import {
  CarouselModes,
  DefaultFilterOptions,
  DefaultPageLimit,
  ProductFormatAvailability
} from '@/misc/constants';

import ShareButton from '@/components/Elements/ShareButton';
import PreviewItems from '@/components/PreviewItems/Index';
import Carousel from '@/components/Carousel/Index';
import TitleWithBackground from '@/components/TitleWithBackground';

import { fetchProducts } from '@/services/products';
import {
  fetchProductsLatest,
  fetchProductsTrending
} from '@/services/statistics';
import formatMoney from '@/misc/formatMoney';
import filterProductsByRegion from '@/misc/filterProductByRegion';
import getDefaultProductFormat from '@/misc/getDefaultProductFormat';
import getFormatPrice from '@/misc/getFormatPrice';
import getCategory from '@/misc/getCategory';
import AdBanner from '@/components/AdBanner/index.vue';

export default {
  name: 'AllProducts',

  components: {
    AdBanner,
    ShareButton,
    PreviewItemsView: PreviewItems,
    CarouselView: Carousel,
    TitleWithBackground
  },

  asyncData({ $axios, error, store, params, redirect }) {
    const category = getCategory(store, params);
    const region = store.getters['config/region'];
    if (!category?.id) {
      redirect('/');
    }

    return Promise.all([
      fetchProductsTrending($axios, {
        query: {
          $where: {
            formats: {
              $elemMatch: {
                'prices.region': region,
                deletedAt: { $exists: false },
                availability: ProductFormatAvailability.Available
              }
            }
          }
        }
      }),
      fetchProducts($axios, {
        query: {
          $where: {
            categoryIds: { $in: [category?.id] },
            formats: {
              $elemMatch: {
                'prices.region': region,
                deletedAt: { $exists: false },
                availability: ProductFormatAvailability.Available
              }
            }
          },
          $sort: { createdAt: -1 },
          $limit: DefaultPageLimit
        }
      }),
      fetchProductsLatest($axios, {
        query: {
          $where: {
            categoryIds: { $in: [category?.id] },
            formats: {
              $elemMatch: {
                'prices.region': region,
                deletedAt: { $exists: false },
                availability: ProductFormatAvailability.Available
              }
            }
          }
        }
      })
    ])
      .then((values) => {
        return {
          products: filterProductsByRegion(
            get(values, '[1].data'),
            store.getters['config/region']
          ),
          productsTotal: get(values, '[1].total'),
          trendingProducts: filter(
            filterProductsByRegion(
              get(values, '[0].data'),
              store.getters['config/region']
            ),
            (product) => product.categories?.includes(category?.id)
          ),
          newProducts: filterProductsByRegion(
            get(values, '[2].data'),
            store.getters['config/region']
          ),
          category
        };
      })
      .catch(() => {
        error({ statusCode: 500 });
      });
  },

  data() {
    return {
      CarouselModes,
      buyNowLoading: false,
      newProductsLoading: false,
      filteredProductsLoading: false,
      isNothingFound: false,
      filterValue: DefaultFilterOptions[0].value,
      skip: DefaultPageLimit
    };
  },
  head: {
    title: 'Discover our books | Found',
    meta: [
      {
        hid: 'description',
        name: 'description',
        content:
          'Found it! - Discover hundreds of book titles, from top social creators, to tickle your tastebuds with their recipes'
      }
    ]
  },

  computed: {
    ...mapGetters({
      currency: 'config/currency',
      region: 'config/region',
      isBannerEnabled: 'config/isBannerEnabled'
    }),
    categoryUrl() {
      return (this.$route.path || 'products') + '/';
    },
    textAbove() {
      const lifestyleId = '650ac42abb3e84ce2641ba12';
      return this.category?.id === lifestyleId
        ? 'Whatever your lifestyle'
        : 'Whatever food you enjoy';
    },
    header() {
      const lifestyleId = '650ac42abb3e84ce2641ba12';
      const categoryName =
        this.category?.id === lifestyleId ? 'Books' : this.category?.name;
      return `Find ${categoryName} that are right for you`;
    }
  },

  methods: {
    handleProductClick(params) {
      this.$router.push({
        path: `${this.categoryUrl}${
          this.$get(params, 'item.id') || this.$get(params, 'id')
        }`
      });
    },
    getProductFormatPrice(product) {
      const defaultFormat = getDefaultProductFormat(product);
      return formatMoney(
        getFormatPrice(defaultFormat, this.region)?.amount,
        this.currency
      );
    },
    getNewProducts() {
      this.newProductsLoading = true;
      const query = {
        $where: this.category
          ? {
              categoryIds: { $in: [this.category.id] }
            }
          : {},
        $sort: { createdAt: -1 },
        $limit: DefaultPageLimit,
        $skip: this.skip || 0
      };
      query.$where = {
        ...query.$where,
        formats: {
          $elemMatch: {
            'prices.region': this.region,
            deletedAt: { $exists: false },
            availability: ProductFormatAvailability.Available
          }
        }
      };
      fetchProducts(this.$axios, {
        query: this.getProductsQueryWithFilter(query)
      })
        .then(({ data: newProducts, total: productsTotal }) => {
          this.products = filterProductsByRegion(
            [...this.products, ...newProducts],
            this.region
          );
          this.productsTotal = productsTotal;
          this.skip += DefaultPageLimit;
        })
        .catch((err) => {
          this.$showToastMessage(
            this.$get(err, 'response.data.message'),
            'Error',
            "Can't fetch new products"
          );
        })
        .finally(() => (this.newProductsLoading = false));
    },
    handleFilterValueChange(value) {
      this.filterValue = value;
      this.filteredProductsLoading = true;
      const query = {
        $where: this.category
          ? {
              categoryIds: {
                $in: [this.category.id]
              }
            }
          : {},
        $sort: { createdAt: -1 },
        $limit: DefaultPageLimit
      };
      query.$where = {
        ...query.$where,
        formats: {
          $elemMatch: {
            'prices.region': this.region,
            deletedAt: { $exists: false },
            availability: ProductFormatAvailability.Available
          }
        }
      };
      fetchProducts(this.$axios, {
        query: this.getProductsQueryWithFilter(query)
      })
        .then(({ data: filteredProducts, total: productsTotal }) => {
          this.isNothingFound = isEmpty(filteredProducts);
          this.products = filterProductsByRegion(filteredProducts, this.region);
          this.productsTotal = productsTotal;
        })
        .catch((err) => {
          this.$showToastMessage(
            this.$get(err, 'response.data.message'),
            'Error',
            "Can't fetch products"
          );
        })
        .finally(() => (this.filteredProductsLoading = false));
    },
    getProductsQueryWithFilter(query) {
      if (this.filterValue !== 'all') {
        return {
          ...query,
          $where: {
            ...query.$where,
            name: { $regex: `^${this.filterValue}`, $options: 'i' }
          }
        };
      }
      return query;
    }
  }
};
</script>

<style lang="scss" scoped>
.products {
  &__top-banner {
    position: relative;
    display: flex;
    justify-content: flex-end;
    flex-direction: column;
    min-height: 255px;
    background-color: $primary-color;
    color: $dark-color;
    padding-bottom: 35px;
    padding-top: 50px;

    h1 {
      position: relative;
      font-family: 'PPRader-Bold', sans-serif;
      font-size: 40px;
      line-height: 46px;
      text-transform: uppercase;
    }

    @media (max-width: $screen-width-xs) {
      padding-top: 75px;
      padding-left: 15px;
      padding-bottom: 50px;
    }
  }

  &__category-desc {
    padding-top: 35px;
    font-weight: 400;
  }
}

.banner {
  width: 100%;
  min-height: unset;
  height: unset;
  padding: 0;
  background-color: transparent;

  @media (max-width: $screen-width-xs) {
    max-height: unset;
    height: unset;
  }
}
</style>
